import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, Loader2, RefreshCw, Shield } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';

type KeyStatus = {
  hasKey: boolean;
  keyPrefix?: string;
  createdAt?: string;
  lastUsedAt?: string;
};

export function KeyCard({
  status,
  onGenerate,
  loading,
}: {
  status: KeyStatus;
  onGenerate: () => Promise<string>;
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Card className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Lock className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            {status.hasKey ? (
              <>
                <Badge variant="secondary" className="flex-shrink-0">
                  Active
                </Badge>
                {status.keyPrefix && (
                  <span className="text-sm text-muted-foreground truncate">
                    Prefix: <code className="truncate">{status.keyPrefix}</code>
                  </span>
                )}
              </>
            ) : (
              <span className="text-sm text-muted-foreground">
                No API key yet
              </span>
            )}
          </div>
          <Button
            variant={status.hasKey ? 'outline' : 'default'}
            onClick={() => setOpen(true)}
            disabled={loading}
            size="sm"
          >
            {loading ? 'Working…' : status.hasKey ? 'Rotate key' : 'Create key'}
          </Button>
        </div>
      </Card>

      {open && (
        <KeyRollModal
          hasKey={status.hasKey}
          onClose={() => setOpen(false)}
          onGenerate={onGenerate}
        />
      )}
    </>
  );
}

function KeyRollModal({
  hasKey,
  onGenerate,
  onClose,
  disableGenerate = false,
}: {
  hasKey: boolean;
  onGenerate: () => Promise<string>;
  onClose: () => void;
  disableGenerate?: boolean;
}) {
  const [phase, setPhase] = useState<'confirm' | 'generating' | 'reveal'>(
    'confirm'
  );
  const [plaintext, setPlaintext] = useState('');
  const [ack, setAck] = useState(false);
  const [err, setErr] = useState<string>('');

  const start = async () => {
    setErr('');
    setPhase('generating');
    const startTime = Date.now();

    try {
      const key = await onGenerate();

      const minDuration = 2000;
      const elapsed = Date.now() - startTime;
      const delay = Math.max(0, minDuration - elapsed);

      setTimeout(() => {
        setPlaintext(key);
        setPhase('reveal');
      }, delay);
    } catch (e: unknown) {
      setErr((e as Error)?.message || 'Failed to generate key');
      setPhase('confirm');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4 z-50">
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-xl p-6 shadow-xl">
        {phase === 'confirm' && (
          <>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="h-5 w-5" />
              <h3 className="text-lg font-semibold">
                {hasKey ? 'Rotate API key?' : 'Create API key?'}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {hasKey
                ? 'Rotating will revoke your current key immediately. You must paste the new key into the Cursor extension.'
                : 'You’ll see the key once. Copy and store it securely.'}
            </p>
            {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={start} disabled={disableGenerate}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {hasKey ? 'Yes, rotate' : 'Create key'}
              </Button>
            </div>
          </>
        )}

        {phase === 'generating' && <GeneratingPanel />}

        {phase === 'reveal' && (
          <RevealPanel
            plaintext={plaintext}
            ack={ack}
            setAck={setAck}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function GeneratingPanel() {
  const lines = useMemo(
    () => Array.from({ length: 6 }, () => randomCipher(42)),
    []
  );
  return (
    <div className="text-center">
      <div className="inline-flex items-center gap-2 mb-3">
        <Loader2 className="h-5 w-5 animate-spin" />
        <h3 className="text-lg font-semibold">Generating secure key…</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Hardening entropy, sealing vault, signing prefix…
      </p>
      <div className="bg-slate-100 dark:bg-slate-800 rounded-md p-3 font-mono text-xs leading-5 text-slate-600 dark:text-slate-300 animate-pulse">
        {lines.map((l, i) => (
          <div key={i} className="truncate">
            {l}
          </div>
        ))}
      </div>
    </div>
  );
}

function randomCipher(n: number) {
  const alphabet = '█▓▒░#@$&%*+=-_<>{}[]/\\|~^';
  let s = '';
  for (let i = 0; i < n; i++)
    s += alphabet[(Math.random() * alphabet.length) | 0];
  return s;
}

/**
 * One-time plaintext reveal: the key, copy-to-clipboard, a .txt download, and a
 * "Done" button that stays disabled until the user acknowledges they will not
 * see the key again.
 *
 * Exported (additively — no behavior change) so the management API keys card
 * reuses this exact affordance instead of duplicating it. Callers own the
 * surrounding overlay and the `ack` state.
 */
export function RevealPanel({
  plaintext,
  ack,
  setAck,
  onClose,
}: {
  plaintext: string;
  ack: boolean;
  setAck: (v: boolean) => void;
  onClose: () => void;
}) {
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  useEffect(() => {
    if (!showCopiedMessage) return;

    const timeoutId = setTimeout(() => setShowCopiedMessage(false), 2000);
    return () => clearTimeout(timeoutId);
  }, [showCopiedMessage]);

  const downloadTxt = () => {
    const blob = new Blob([plaintext], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kirin_api_key.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <h3 className="text-lg font-semibold mb-2">Your new API key</h3>
      <p className="text-sm text-muted-foreground mb-3">
        This is the only time we'll show it. Copy and store it securely.
      </p>
      <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-md font-mono text-sm mb-3 break-all">
        {plaintext}
      </div>
      <div className="space-y-3 mb-4">
        <div className="flex gap-2">
          <CopyToClipboardButton
            text={plaintext}
            feedbackMode="external"
            onCopySuccess={() => setShowCopiedMessage(true)}
          >
            Copy
          </CopyToClipboardButton>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" onClick={downloadTxt}>
                  Download .txt
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Download key as a text file</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {showCopiedMessage && (
          <HighlightBox variant="success" radius="md" className="text-sm">
            API Key copied to clipboard
          </HighlightBox>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm mb-4">
        <input
          type="checkbox"
          className="accent-current"
          checked={ack}
          onChange={e => setAck(e.target.checked)}
        />
        I've saved this key. I understand I won't be able to see it again.
      </label>
      <div className="flex justify-end">
        <Button onClick={onClose} disabled={!ack}>
          Done
        </Button>
      </div>
    </>
  );
}

export { KeyRollModal };
